/**
 * ToteCraft studio — reusable confirm modal (replaces window.confirm).
 * Markup: #bag-studio-confirm-modal (Bootstrap 5), same pattern as Portrait Studio.
 */

/**
 * @param {HTMLElement} root
 * @returns {{
 *   confirm: (options?: {
 *     title?: string,
 *     message?: string,
 *     confirmLabel?: string,
 *     cancelLabel?: string
 *   }) => Promise<boolean>
 * }}
 */
export function bindConfirmModal(root) {
    const modalEl = root.querySelector('#bag-studio-confirm-modal');
    const titleEl = root.querySelector('#bag-studio-confirm-modal-label');
    const messageEl = root.querySelector('#bag-studio-confirm-modal-message');
    const cancelBtn = root.querySelector('#bag-studio-confirm-cancel');
    const confirmBtn = root.querySelector('#bag-studio-confirm-ok');

    /** @type {((ok: boolean) => void)|null} */
    let resolver = null;
    let resolved = false;
    let bound = false;

    function settle(ok) {
        if (typeof resolver === 'function') {
            resolver(ok);
            resolver = null;
        }
        resolved = true;

        if (modalEl && typeof window !== 'undefined' && window.bootstrap) {
            const instance = window.bootstrap.Modal.getInstance(modalEl);
            if (instance) {
                instance.hide();
            }
        }
    }

    function bindOnce() {
        if (bound) {
            return;
        }
        bound = true;

        confirmBtn?.addEventListener('click', () => {
            settle(true);
        });
        cancelBtn?.addEventListener('click', () => {
            settle(false);
        });
        modalEl?.addEventListener('hidden.bs.modal', () => {
            if (!resolved && typeof resolver === 'function') {
                settle(false);
            }
            resolved = false;
        });
    }

    return {
        /**
         * @param {{ title?: string, message?: string, confirmLabel?: string, cancelLabel?: string }} [options]
         * @returns {Promise<boolean>}
         */
        confirm(options) {
            const opts = options || {};
            bindOnce();

            const title = opts.title || 'Are you sure?';
            const message = opts.message || '';
            const cancelLabel = opts.cancelLabel || 'Cancel';
            const confirmLabel = opts.confirmLabel || 'Continue';

            return new Promise((resolve) => {
                const fallback = message ? title + '\n\n' + message : title;
                const bootstrapModal = typeof window !== 'undefined'
                    && window.bootstrap
                    && typeof window.bootstrap.Modal === 'function'
                    ? window.bootstrap.Modal
                    : null;

                if (!modalEl || !bootstrapModal) {
                    resolve(window.confirm(fallback));
                    return;
                }

                if (titleEl) {
                    titleEl.textContent = title;
                }
                if (messageEl) {
                    messageEl.textContent = message;
                }
                if (cancelBtn) {
                    cancelBtn.textContent = cancelLabel;
                }
                if (confirmBtn) {
                    confirmBtn.textContent = confirmLabel;
                }

                resolved = false;
                resolver = resolve;
                try {
                    bootstrapModal.getOrCreateInstance(modalEl).show();
                } catch {
                    resolver = null;
                    resolved = true;
                    resolve(window.confirm(fallback));
                }
            });
        },
    };
}
